import React, { useEffect, useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Box,
  Radio,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  MenuItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import WoodSupplierTaskService from "services/WoodSupplierTaskService";
import { SnackbarContext } from "context/snackBarContext"; // Add this import

// Modified UserSelectionModal component to correctly handle both users and selected suppliers
const UserSelectionModal = ({
  open,
  onClose,
  onConfirm,
  loading: submitLoading,
  users: externalUsers = [],
  woodSuppliers = []
}) => {
  const { t } = useTranslation();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState(["Wood Monitor", "Field Technician", "FT Plantix"]);
  const availableRoles = ["Wood Monitor", "Field Technician", "FT Plantix"];
  // Get context for alerts instead of using Snackbar
  const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);

  // Add state for confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);


  // Filter users based on selected roles and search query
  const filteredUsers = users?.filter(user => {
    return users;
  });
  // Clear selection when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedUserId("");
      setSearchQuery("");
      setSelectedRoles(["Wood Monitor", "Field Technician", "FT Plantix"]);
    }
  }, [open]);

  // Debounce effect for search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Fetch users when modal opens or search/role filters change
  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open, debouncedSearchQuery, selectedRoles]); // Changed searchQuery to debouncedSearchQuery

  // Handle role selection change
  const handleRoleChange = (event) => {
    const value = event.target.value;

    if (value === "all") {
      // Toggle between all roles and no roles
      setSelectedRoles(selectedRoles.length === availableRoles.length ? [] : [...availableRoles]);
    } else {
      // Check if role is already selected
      const currentIndex = selectedRoles.indexOf(value);
      const newSelectedRoles = [...selectedRoles];

      if (currentIndex === -1) {
        // Add role
        newSelectedRoles.push(value);
      } else {
        // Remove role
        newSelectedRoles.splice(currentIndex, 1);
      }

      setSelectedRoles(newSelectedRoles);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      // Only add roles parameter if there are selected roles
      if (selectedRoles.length > 0) {
        params.roles = selectedRoles.join(',');
      }

      // Add search parameter if there's a search query
      if (debouncedSearchQuery.trim() !== "") {
        params.keyword = debouncedSearchQuery;
      }

      const response = await WoodSupplierTaskService.getUsers(params);

      if (response?.data?.response) {
        const fetchedUsers = response.data.response.map(user => ({
          oneId: user.batOneId || "",
          name: user?.name || "",
          email: user?.email || "",
          roles: user?.profileName || "",
          location: user?.locationName || ""
        }));
        setUsers(fetchedUsers);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
  };

  // Modified confirm handler to show confirmation dialog
  const handleConfirm = () => {
    if (selectedUserId) {
      // Find the selected user's details
      const user = filteredUsers.find(user => user.oneId === selectedUserId);
      if (user) {
        setSelectedUserDetails(user);
        setConfirmDialogOpen(true);
      } else {
        // Use the context functions instead of local state
        setSeverity("error");
        setMessage("Selected user not found");
        setOpenAlert(true);
      }
    } else {
      // Use the context functions instead of local state
      setSeverity("error");
      setMessage("Please select a user to assign");
      setOpenAlert(true);
    }
  };

  // Modify handleFinalConfirm to match ApproveModal style
  const handleFinalConfirm = () => {
    setConfirmDialogOpen(false);
    onConfirm(selectedUserId);
  };

  
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            padding: 0,
            minHeight: "500px",
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 16px",
            backgroundColor: "#102A62",
            margin: 0,
            width: "100%",
            height: "50px",
            boxSizing: "border-box",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: "#FFFFFF",
              fontFamily: "Montserrat",
              fontWeight: 600,
              fontSize: "20px",
              textAlign: "center",
            }}
          >
            Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "20px",
            overflow: "hidden",
            marginTop: "10px",
            display: "flex",
            flexDirection: "column",
            gap: "20px"
          }}
        >
          <Box sx={{ backgroundColor: "#EFEFEF", display: "flex", padding: "16px", borderRadius: "5px", margingtop: "10px" }}>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                width: "100%",
                borderRadius: "5px",
              }}
            >
              <Box sx={{ width: "200px" }}>
                <TextField
                  select
                  label={t("woodSupplierTask.role")}
                  fullWidth
                  value={selectedRoles}
                  onChange={(event) => {
                    const selectedValue = event.target.value;

                    if (selectedValue.includes("all")) {
                      setSelectedRoles(
                        selectedRoles.length === availableRoles.length ? [] : [...availableRoles]
                      );
                    } else {
                      setSelectedRoles(selectedValue);
                    }
                  }}
                  placeholder="Select roles"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "46px",
                      borderRadius: "5px"
                    }
                  }}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => selected.join(", "),
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 250,
                          overflowY: "auto",
                        },
                      },
                    },
                  }}
                >
                  {availableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedRoles.indexOf(role) > -1}
                        />
                      </ListItemIcon>
                      <ListItemText primary={role} />
                    </MenuItem>
                  ))}
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedRoles.length === availableRoles.length}
                        indeterminate={
                          selectedRoles.length > 0 &&
                          selectedRoles.length < availableRoles.length
                        }
                      />
                    </ListItemIcon>
                    <ListItemText primary="All" />
                  </MenuItem>
                </TextField>
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label={t("woodSupplierTask.search")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder=""
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "46px",
                      borderRadius: "5px"
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              flex: 1,
              overflowY: "auto",
              border: "none",
              boxShadow: "none",
              maxHeight: "calc(100vh - 350px)", // Add max height to enable scrolling
              position: "relative" // Add position relative
            }}
          >
            <Table
              stickyHeader
              sx={{
                borderCollapse: "separate",
                borderSpacing: "0 10px",
                padding: "10px",
                "& .MuiTableHead-root": {
                  position: "sticky",
                  top: 0,
                  zIndex: 3, // Higher than row hover shadow
                  backgroundColor: "#ffffff"
                }
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      borderBottom: "5px solid #102a62",
                      backgroundColor: "#ffffff !important",
                      color: "#3B454F",
                      paddingLeft: { xs: 1, sm: 2 },
                      paddingRight: { xs: 1, sm: 2 },
                      borderRight: "none",
                      borderLeft: "none",
                      position: "sticky", // Add sticky positioning
                      top: 0, // Stick to top
                      zIndex: 3 // Higher than row hover shadow
                    },
                    "& th:first-of-type": {
                      paddingLeft: 2
                    },
                    "& th:last-of-type": {
                      paddingRight: 2
                    }
                  }}
                >
                  <TableCell sx={{ width: "50px", p: 1 }}></TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>One ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>E-Mail ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Roles</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow sx={{ gap: 2 }}>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <TableRow
                      key={user.oneId}
                      hover
                      sx={{
                        '&:hover': {
                          '&::after': {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.25)', // Stronger shadow on hover
                          }
                        },
                        cursor: 'pointer',
                        position: 'relative',
                        // Remove overflow hidden that might be clipping the edges
                        // overflow: 'hidden', 
                        // Remove borders between cells
                        '& .MuiTableCell-root':{
                          padding: '12px !important',
                        },
                        '& td': {
                          border: 'none',
                          backgroundColor: 'white',
                          position: 'relative',
                          zIndex: 2, // Higher z-index for content
                        },
                        // First cell needs proper padding
                        '& td:first-of-type': {
                          paddingLeft: '12px'
                        },
                        // Last cell needs proper padding
                        '& td:last-of-type': {
                          paddingRight: '12px'
                        },
                        // Add a pseudo-element for the background box with shadow
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          // Extend beyond the natural boundaries of cells
                          top: 0,
                          bottom: 0,
                          left: '1px', // Adjust slightly to ensure left border is visible
                          right: '1px', // Adjust slightly to ensure right border is visible
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          // Use a more pronounced shadow with wider spread
                          boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                          zIndex: 1,
                          pointerEvents: 'none',
                          transition: 'box-shadow 0.2s ease'
                        }
                      }}
                      onClick={() => handleUserSelect(user.oneId)}
                    >
                      <TableCell sx={{ p: 1 }}>
                        <Radio
                          checked={selectedUserId === user.oneId}
                          onChange={() => handleUserSelect(user.oneId)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{user.oneId}</TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.roles}</TableCell>
                      <TableCell>{user.location}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "10px 20px 20px 20px",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: "#6B7280",
              color: "white",
              textTransform: "none",
              fontFamily: "Montserrat",
              "&:hover": {
                backgroundColor: "#4B5563",
              },
              width: "120px",
              height: "46px"
            }}
          >
            BACK
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={submitLoading}
            sx={{
              backgroundColor: "#004F9F",
              color: "white",
              textTransform: "none",
              fontFamily: "Montserrat",
              "&:hover": {
                backgroundColor: "#003B75",
              },
              "&.Mui-disabled": {
                color: "white",
                backgroundColor: "#004F9F",
                opacity: 0.5,
              },
              width: "120px",
              height: "46px"

            }}
            startIcon={submitLoading && <CircularProgress size={20} color="inherit" />}
          >
            ASSIGN
          </Button>
        </DialogActions>
      </Dialog>

      {/* Final Confirmation Dialog - Updated to match ApproveModal style */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            padding: 0,
            minHeight: 'fit-content',
            maxHeight: 'fit-content'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 16px',
            backgroundColor: '#102A62',
            margin: 0,
            width: '100%',
            height: '50px',
            boxSizing: 'border-box',
            textAlign: 'center'
          }}
        >
          <Typography
            sx={{
              color: '#FFFFFF',
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '20px',
              textAlign: 'center'
            }}
          >
            Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: '20px',
            overflow: 'auto',
            marginTop: '20px',
            maxHeight: '500px'
          }}
        >
          {selectedUserDetails && (
            <>
              <Typography
                sx={{
                  color: '#000000DE',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  lineHeight: '24px',
                  marginBottom: '20px'
                }}
              >
                Are you sure you want to assign the selected Wood Suppliers to:
              </Typography>
              <Box sx={{ mb: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                <Typography sx={{ mb: 0.5 }}><strong>Name:</strong> {selectedUserDetails.name}</Typography>
                <Typography sx={{ mb: 0.5 }}><strong>One ID:</strong> {selectedUserDetails.oneId}</Typography>
                <Typography><strong>Role:</strong> {selectedUserDetails.roles}</Typography>
              </Box>
              <Typography
                sx={{
                  color: '#000000DE',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontWeight: 500,
                  marginBottom: '10px'
                }}
              >
                Selected Wood Suppliers:
              </Typography>
              <Box sx={{
                maxHeight: "200px",
                overflow: "auto",
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
                mb: 2
              }}>
                {woodSuppliers.filter(supplier => supplier.checked && !supplier.assignedTo).length > 0 ? (
                  woodSuppliers.filter(supplier => supplier.checked && !supplier.assignedTo).map((supplier, index) => (
                    <Typography key={index} sx={{ mb: 0.5, fontFamily: 'Montserrat' }}>
                      {index + 1}. {supplier.woodSupplierName || supplier.name || "Unnamed Supplier"}
                      {supplier.wsPermanentCode && ` (${supplier.wsPermanentCode})`}
                    </Typography>
                  ))
                ) : (
                  <Typography sx={{ fontFamily: 'Montserrat' }}>No wood suppliers selected</Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            padding: '0 20px 20px 20px',
            justifyContent: 'center',
            gap: 6
          }}
        >
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="outlined"
            sx={{
              color: 'white',
              borderColor: '#62636C',
              background: '#62636C',
              textTransform: 'none',
              fontFamily: 'Montserrat',
              height: "46px",
              '&:hover': {
                color: 'white',
                background: '#62636C',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFinalConfirm}
            variant="contained"
            sx={{
              backgroundColor: '#004F9F',
              color: 'white',
              textTransform: 'none',
              fontFamily: 'Montserrat',
              '&:hover': {
                backgroundColor: '#004F9F'
              },
              minWidth: '100px',
              height: "46px"

            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserSelectionModal;